// Tipos compartilhados para configuração de parâmetros de templates

export interface ParameterConfig {
  index: number;
  type: "fixed" | "dynamic";
  value: string;
  dynamicField:
    | "clientName"
    | "clientPhone"
    | "companyName"
    | "attendantName"
    | "scheduledDate"
    | null;
}

export interface TemplateParameterInfo {
  templateId: string;
  templateName: string;
  requiredParametersCount: number;
  requiredParameters: Array<{
    component: string;
    description: string;
    example: string;
    parameterCount: number;
  }>;
  summary: string;
}

export interface TemplateConfigProps {
  templateParameters: TemplateParameterInfo | null;
  loadingParameters: boolean;
  attendantName: string;
  setAttendantName: (name: string) => void;
  parameterConfigs: ParameterConfig[];
  setParameterConfigs: (configs: ParameterConfig[]) => void;
  accountName?: string;
  onParameterChange?: (configs: ParameterConfig[]) => void;
}

// Mapeamento de campos dinâmicos para placeholders
export const DYNAMIC_FIELD_MAPPING = {
  clientName: "{{1}}",
  clientPhone: "{{2}}",
  companyName: "{{3}}",
  attendantName: "{{4}}",
  scheduledDate: "{{5}}",
} as const;

// Opções de campos dinâmicos para Select
export const DYNAMIC_FIELD_OPTIONS = [
  { value: "clientName", label: "Nome do Cliente" },
  { value: "clientPhone", label: "Telefone do Cliente" },
  { value: "companyName", label: "Nome da Empresa" },
  { value: "attendantName", label: "Nome do Atendente" },
  { value: "scheduledDate", label: "Data do Agendamento" },
] as const;

// Função utilitária para inicializar configurações de parâmetros
export const initializeParameterConfigs = (
  requiredCount: number,
): ParameterConfig[] => {
  const configs: ParameterConfig[] = [];
  for (let i = 0; i < requiredCount; i++) {
    // Não usar mapeamento fixo por índice - deixar o usuário escolher
    configs.push({
      index: i,
      type: "dynamic",
      value: "",
      dynamicField: "clientName", // Valor padrão, usuário pode alterar
    });
  }
  return configs;
};

// Função utilitária para processar parâmetros finais
export const processFinalParameters = (
  parameterConfigs: ParameterConfig[],
  requiredCount: number,
): string[] => {
  if (requiredCount === 0) return [];

  // Sempre usar modo avançado - configurações personalizadas
  return parameterConfigs.map((config) => {
    if (config.type === "fixed") {
      return config.value;
    } else {
      // Para modo dinâmico, enviar placeholder que será substituído no backend
      return DYNAMIC_FIELD_MAPPING[config.dynamicField] || "{{DYNAMIC_VALUE}}";
    }
  });
};

// Função utilitária para gerar preview de mensagem
export const generateMessagePreview = (
  configs: ParameterConfig[],
  attendantName: string,
): string => {
  if (configs.length === 0) return "";

  let preview = "Template com parâmetros: ";
  configs.forEach((config, index) => {
    let value = "";
    if (config.type === "fixed") {
      value = config.value || `[Parâmetro ${index + 1}]`;
    } else {
      switch (config.dynamicField) {
        case "clientName":
          value = "[Nome do Cliente]";
          break;
        case "clientPhone":
          value = "[Telefone do Cliente]";
          break;
        case "companyName":
          value = "[Nome da Empresa]";
          break;
        case "attendantName":
          value = attendantName || "[Nome do Atendente]";
          break;
        case "scheduledDate":
          value = "[Data do Agendamento]";
          break;
        default:
          value = "[Valor]";
      }
    }
    preview += `{{${index + 1}: ${value}}} `;
  });

  return preview;
};
